import { useDispatch, useSelector } from "react-redux";

import { COUNTRIES, FILTER_OPTIONS, FILTER_TYPES } from "@constants/dashboard";
import { LocationPositionType } from "@models/components/organisation.model";
import { useOrganisationFilterParams } from "@models/hooks/useOrganisationFilter.model";
import { applyLocalization } from "@services/common.service";
import dashboardSlice from "@slices/dashboard.slice";
import { RootState } from "@store/index";

export const useOrganisationFilter = (params: useOrganisationFilterParams) => {
  const {
    cityName,
    clubName,
    country,
    filterChange,
    filterType,
    organisationData,
    setCityName,
    setClubName,
    setCountry,
    setFilterType,
  } = params;
  const dispatch = useDispatch();
  const isCity = filterType === FILTER_TYPES.CITY;

  const { city, countryCode } = useSelector((state: RootState) => state.app);

  const {
    city: searchedCity,
    club: searchedClub,
    country: searchedCountryName,
    isCity: isCityOrClub,
    isSearch,
  } = useSelector((state: RootState) => state.dashboard);

  const filterOrganisation = () => {
    return organisationData.filter(data => {
      if (isCity && cityName)
        return (
          searchCountry(data?.address?.country) &&
          toLowerCase(data?.address?.location?.trim())?.startsWith(
            toLowerCase(cityName?.trim())
          )
        );
      else if (clubName) {
        const localizedTitle = applyLocalization(data?.title)?.trim();
        const searchQuery = toLowerCase(clubName?.trim());

        return (
          searchCountry(data?.address?.country) &&
          localizedTitle &&
          searchQuery &&
          toLowerCase(localizedTitle)?.includes(searchQuery)
        );
      } else return searchCountry(data?.address?.country);
    });
  };

  const filterTypeSetting = () => {
    if (!isCityOrClub) {
      setFilterType(FILTER_TYPES.CLUB);
      setClubName(searchedClub);
    } else {
      setFilterType(FILTER_TYPES.CITY);
      setCityName(searchedCity);
    }
  };

  const getDefaultBrowserLocation = (
    setPosition: (value: LocationPositionType) => void,
    setLoader: (value: boolean) => void
  ) => {
    setLoader(true);
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        position => {
          setPosition({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
          setLoader(false);
          if (!searchedCity && !isSearch) setCityName(city);
          if (!searchedCountryName) setCountry(setCountryCode());
        },
        () => {
          setLoader(false);
        }
      );
    }
  };

  const handleSearch = (closeModal: () => void) => {
    dispatch(
      dashboardSlice.actions.setLocation({
        city: isCity ? cityName : "",
        club: !isCity ? clubName : "",
        country: country,
        isCity: isCity,
        isSearch: true,
      })
    );
    closeModal();
    dispatch(
      dashboardSlice.actions.setFilteredOrganisationData({
        filteredOrganisationData: filterOrganisation(),
      })
    );
    filterChange(FILTER_OPTIONS.FILTER);

    if (isCity) setClubName("");
    else setCityName("");
  };

  const resetSearch = (closeModal: () => void) => {
    dispatch(
      dashboardSlice.actions.setLocation({
        city: "",
        club: "",
        country: "",
        isCity: true,
        isSearch: false,
      })
    );
    filterChange(FILTER_OPTIONS.ALL);
    dispatch(
      dashboardSlice.actions.setFilteredOrganisationData({
        filteredOrganisationData: organisationData,
      })
    );
    closeModal();
    setCityName("");
    setClubName("");
    setCountry(COUNTRIES.SWITZERLAND);
    setFilterType(FILTER_TYPES.CITY);
  };

  const searchCountry = (searchCountryName: string) => {
    return toLowerCase(searchCountryName) === toLowerCase(country);
  };

  const setCountryCode = () => {
    if (
      countryCode === COUNTRIES.GERMANY ||
      countryCode === COUNTRIES.SWITZERLAND
    )
      return countryCode;
    else return COUNTRIES.SWITZERLAND;
  };

  const toLowerCase = (item: string) => item?.toLocaleLowerCase();

  return {
    filterOrganisation,
    filterTypeSetting,
    getDefaultBrowserLocation,
    handleSearch,
    isCity,
    resetSearch,
    setCountryCode,
  };
};
